import { computed, inject, Injectable, signal } from '@angular/core';
import { AudioService } from '@core/audio/audio.service';
import { DataReaderService } from '@core/lp/data-reader.service';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { waitForMillis } from '@shared/delay.utils';
import { Uid } from '@typings/id';
import { LpActivity, LpItem, LpMap, LpMissionInfo, LpStory } from '@typings/lp/generated';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  readonly enableDebug = signal(false);
  readonly inventory = signal<LpItem[]>([]);
  readonly inventoryUids = computed(() => this.inventory().map((item) => item.uid as Uid));
  readonly map = signal<LpMap | undefined>(undefined);
  readonly story = signal<LpStory | undefined>(undefined);
  readonly mission = signal<LpMissionInfo | undefined>(undefined);
  readonly activity = signal<LpActivity | undefined>(undefined);
  readonly item = signal<LpItem | undefined>(undefined); // for item content preview
  readonly mapUid = computed(() => this.map()?.uid as Uid | undefined);
  readonly storyUid = computed(() => this.story()?.uid as Uid | undefined);
  readonly missionUid = computed(() => this.mission()?.uid as Uid | undefined);
  readonly activityUid = computed(() => this.activity()?.uid as Uid | undefined);
  // TODO: move this to debug service
  readonly logLines = signal<string[]>([]);
  readonly #lpVariablesService = inject(LpVariablesService);
  readonly #audioService = inject(AudioService);
  private registeredGames = 0;
  #dataReaderService = inject(DataReaderService);

  // TODO: figure out if there is a more intuitive way to handle this
  subscribe() {
    this.inventory();
    this.map();
    this.mission();
    this.story();
    this.activity();
    this.item();
    this.#lpVariablesService.subscribe();
  }

  public registerGame() {
    this.registeredGames++;
  }

  public unregistergame() {
    this.registeredGames--;
  }

  public async playStory(story: LpStory, waitUntilComplete?: boolean): Promise<void> {
    await this.setStory(story.uid as Uid);
    if (waitUntilComplete) {
      if (this.registeredGames === 0) return;

      // TODO: ugly polling
      while (true) {
        if (this.story() === undefined) return;
        await waitForMillis(50);
      }
    }
    return;
  }

  public async showItemContent(item: LpItem, waitUntilClosed: boolean): Promise<void> {
    await this.setItem(item.uid as Uid);
    if (waitUntilClosed) {
      if (this.registeredGames === 0) return;

      // TODO: ugly polling
      while (true) {
        if (this.item() === undefined) return;
        await waitForMillis(50);
      }
    }
    return;
  }

  async setActivity(uid: Uid | undefined): Promise<void> {
    this.#audioService.stopAllLoops();
    await this.#setInstance<LpActivity>(uid, (value) => this.activity.set(value));
  }

  async setMap(uid: Uid | undefined): Promise<void> {
    this.#audioService.stopAllLoops();
    await this.#setInstance<LpMap>(uid, (value) => this.map.set(value));
  }

  async setStory(uid: Uid | undefined): Promise<void> {
    await this.#setInstance<LpStory>(uid, (value) => this.story.set(value));
  }

  async setItem(uid: Uid | undefined): Promise<void> {
    await this.#setInstance<LpItem>(uid, (value) => this.item.set(value));
  }

  async setMission(uid: Uid | undefined): Promise<void> {
    await this.#setInstance<LpMissionInfo>(uid, (value) => this.mission.set(value));
  }

  async setInventory(inventory: Uid[]): Promise<void> {
    const items = await Promise.all(inventory.map(async (uid) => (await this.#dataReaderService.getInstanceByUid(uid)) as LpItem));
    this.inventory.set(items);
  }

  public removeInventoryItemAt(index: number): void {
    const items = this.inventory();
    if (index < 0 || index >= items.length) return;

    const newItems = [...items.slice(0, index), ...items.slice(index + 1)];
    this.inventory.set(newItems);
  }

  log(message: string) {
    this.logLines.set([message, ...this.logLines()]);
  }

  async #setInstance<T>(uid: Uid | undefined, setter: (value: T | undefined) => void): Promise<void> {
    if (!uid) {
      setter(undefined);
      return;
    }
    const instance = await this.#dataReaderService.getInstanceByUid(uid);

    // NOTE: this is a workaround for the game component reusing the same component instance, this way it will instantiate a new component when the map/mission/whatever changes to another map/mission/whatever
    setter(undefined);
    await waitForMillis(1);

    setter(instance as T);
  }
}
